import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Button, Col, ControlLabel, FormGroup, Modal, Row } from 'react-bootstrap';
import DateTime from 'react-datetime';
import { useTranslation } from 'react-i18next';
import { CustomSelect } from '../../../components/CustomControls/CustomSelect';
import { visitOffsetType, visitTypesString } from '../../../variables/Enums';

/**
 * Modal to edit the date offset configuration for a single visit per patient.
 *
 * Props:
 *  - isOpen       {boolean}
 *  - toggle       {() => void}
 *  - visit        {object}  the visit template for this column
 *  - patient      {object}  the patient row
 *  - allVisits    {array}   all visit templates in the current view (for origin dropdown)
 *  - currentOverride {object|null}  existing override if any: { originCrossVersionId, offset, offsetType, manualReferenceDate }
 *  - onSave       {(override) => void}
 */
export const VisitOffsetEditModal = ({ isOpen, toggle, visit, patient, allVisits, currentOverride, onSave }) => {
    const { t } = useTranslation();

    const getVisitTitle = (title) => {
        if (title === 'ExtraProtocolVisit') return t('protocols.visitTracking.visitTypes.extraProtocol');
        if (title === 'Note') return t('protocols.visitTracking.visitTypes.note');
        if (title === 'EarlyCompletion') return t('protocols.visitTracking.visitTypes.earlyCompletion');
        return title;
    };

    const [useManualDate, setUseManualDate] = useState(false);
    const [manualReferenceDate, setManualReferenceDate] = useState(null);
    const [originVisit, setOriginVisit] = useState(null);
    const [offset, setOffset] = useState('');
    const [offsetType, setOffsetType] = useState(null);
    const [changeReason, setChangeReason] = useState('');
    const [errors, setErrors] = useState({});

    // Available origin visits: only templates that come before the current visit and have been executed (have a date),
    // plus ad-hoc visits (type > 0) that have been executed
    const executedTemplateVisits = allVisits
        .filter(v => v.crossVersionId !== visit?.crossVersionId && v.order < (visit?.order ?? 0))
        .map(v => {
            const executedVisit = patient?.visits?.find(pv => pv.crossVersionId === v.crossVersionId);
            return {
                id: v.crossVersionId,
                crossVersionId: (v.title === visitTypesString.ExtraProtocolVisit || v.title === visitTypesString.EarlyCompletion || v.title === visitTypesString.Note) ? v.id : v.crossVersionId,
                title: `${getVisitTitle(v.title)} ${executedVisit?.date ? ` (${moment(executedVisit.date).format('DD/MMM/YYYY')})` : ` (${t('protocols.visitTracking.pendingCalculation')})`}`,
                date: executedVisit?.date ?? null,
            };
        });

    const adhocVisits = (patient?.visits ?? [])
        .filter(v => v.type > 0 && v.date)
        .map(v => ({
            id: (v.title === visitTypesString.ExtraProtocolVisit || v.title === visitTypesString.EarlyCompletion || v.title === visitTypesString.Note) ? v.id : v.crossVersionId,
            crossVersionId: (v.title === visitTypesString.ExtraProtocolVisit || v.title === visitTypesString.EarlyCompletion || v.title === visitTypesString.Note) ? v.id : v.crossVersionId,
            title: `${getVisitTitle(v.title)} (${moment(v.date).format('DD/MMM/YYYY')})`,
            date: v.date,
        }));

    const originOptions = [...executedTemplateVisits, ...adhocVisits];

    const offsetTypeOptions = [
        { id: visitOffsetType.Days, title: t('protocols.visit.offsetType.0') },
        { id: visitOffsetType.Weeks, title: t('protocols.visit.offsetType.1') },
        { id: visitOffsetType.Months, title: t('protocols.visit.offsetType.2') },
    ];

    // Initialise fields from existing override or template defaults
    useEffect(() => {
        if (!isOpen) return;

        if (currentOverride) {
            setUseManualDate(!!currentOverride.manualReferenceDate);
            setManualReferenceDate(currentOverride.manualReferenceDate ? moment(currentOverride.manualReferenceDate) : null);
            const foundOrigin = originOptions.find(o => o.crossVersionId === currentOverride.originCrossVersionId) ?? null;
            setOriginVisit(foundOrigin);
            setOffset(currentOverride.offset != null ? String(currentOverride.offset) : '');
            const foundType = offsetTypeOptions.find(o => o.id === currentOverride.offsetType) ?? null;
            setOffsetType(foundType);
        } else {
            setUseManualDate(false);
            setManualReferenceDate(null);
            setOriginVisit(null);
            setOffset(visit?.offset != null ? String(visit.offset) : '');
            const foundType = offsetTypeOptions.find(o => o.id === visit?.offsetType) ?? null;
            setOffsetType(foundType);
        }
        setChangeReason('');
        setErrors({});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const validate = () => {
        const newErrors = {};
        if (!changeReason || changeReason.trim() === '') {
            newErrors.changeReason = t('protocols.visitTracking.changeReason') + ' ' + t('commons.required', { defaultValue: 'es obligatorio' });
        }
        if (!useManualDate && !originVisit) {
            newErrors.originVisit = t('protocols.visitTracking.originVisit') + ' ' + t('commons.required', { defaultValue: 'es obligatorio' });
        }
        if (useManualDate && !manualReferenceDate) {
            newErrors.manualReferenceDate = t('protocols.visitTracking.manualReferenceDate') + ' ' + t('commons.required', { defaultValue: 'es obligatorio' });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;

        const override = {
            patientId: patient?.numberInProtocol,
            visitCrossVersionId: visit?.crossVersionId,
            originCrossVersionId: useManualDate ? null : (originVisit?.crossVersionId ?? null),
            offset: offset !== '' ? Number(offset) : null,
            offsetType: offsetType?.id ?? null,
            manualReferenceDate: useManualDate && manualReferenceDate ? moment(manualReferenceDate).toDate() : null,
            audit: { reason: { code: 'visitScheduleOverride' }, comments: changeReason },
        };

        onSave(override);
    };

    if (!isOpen) return null;
    
    return (
        <Modal show={isOpen} onHide={toggle} bsSize="large" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    {t('protocols.visitTracking.editOffset')} — {visit?.title}
                    {patient?.numberInProtocol != null && <small> &middot; {t('protocols.patient.nro')} {patient.numberInProtocol}</small>}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col md={12}>
                        <FormGroup>
                            <input
                                type="checkbox"
                                id="useManualDate"
                                checked={useManualDate}
                                onChange={e => setUseManualDate(e.target.checked)}
                                style={{ marginRight: '8px' }}
                            />
                            <label htmlFor="useManualDate" style={{ fontWeight: 'normal', cursor: 'pointer' }}>
                                {t('protocols.visitTracking.useManualDate')}
                            </label>
                        </FormGroup>
                    </Col>
                </Row>

                {!useManualDate && (
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <ControlLabel>{t('protocols.visitTracking.originVisit')}</ControlLabel>
                                <CustomSelect
                                    name="originVisit"
                                    placeholder={t('commons.select')}
                                    options={originOptions}
                                    value={originVisit}
                                    onChange={(_name, value) => setOriginVisit(value)}
                                    isClearable
                                />
                                {errors.originVisit && <span className="text-danger" style={{ fontSize: '12px' }}>{errors.originVisit}</span>}
                            </FormGroup>
                        </Col>
                    </Row>
                )}

                {useManualDate && (
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <ControlLabel>{t('protocols.visitTracking.manualReferenceDate')}</ControlLabel>
                                <DateTime
                                    value={manualReferenceDate}
                                    onChange={value => setManualReferenceDate(value)}
                                    dateFormat="DD/MM/YYYY"
                                    timeFormat={false}
                                    closeOnSelect
                                    inputProps={{ placeholder: 'DD/MM/YYYY' }}
                                />
                                {errors.manualReferenceDate && <span className="text-danger" style={{ fontSize: '12px' }}>{errors.manualReferenceDate}</span>}
                            </FormGroup>
                        </Col>
                    </Row>
                )}

                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <ControlLabel>Offset</ControlLabel>
                            <input
                                type="number"
                                className="form-control"
                                value={offset}
                                onChange={e => setOffset(e.target.value)}
                                min={0}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <ControlLabel>{t('commons.type')}</ControlLabel>
                            <CustomSelect
                                name="offsetType"
                                placeholder={t('commons.select')}
                                options={offsetTypeOptions}
                                value={offsetType}
                                onChange={(_name, value) => setOffsetType(value)}
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md={12}>
                        <FormGroup>
                            <ControlLabel>{t('protocols.visitTracking.changeReason')} <span className="text-danger">*</span></ControlLabel>
                            <textarea
                                className="form-control"
                                rows={3}
                                value={changeReason}
                                onChange={e => setChangeReason(e.target.value)}
                                placeholder={t('protocols.visitTracking.changeReason')}
                            />
                            {errors.changeReason && <span className="text-danger" style={{ fontSize: '12px' }}>{errors.changeReason}</span>}
                        </FormGroup>
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle="info" onClick={toggle}>{t('buttons.cancel')}</Button>
                <Button bsStyle="success" onClick={handleSave}>{t('buttons.save')}</Button>
            </Modal.Footer>
        </Modal>
    );
};
